<template>
  <div class="pa-4">
    <div v-if="loading">
      <v-skeleton-loader type="card"></v-skeleton-loader>
    </div>
    <div v-else-if="!loading && product">
      <v-card outlined elevation="0" class="pa-2">
        <v-alert class="error text-center white--text" v-if="!product.active && productId != 'new'">
          {{ $t("message.archived") }}
        </v-alert>
        <v-card-text v-if="product.type == 'good'" class="text-center">
          <v-avatar size="250" class="mb-4">
            <ImageDisplay v-if="productImage" :src="productImage" />
            <img v-else :src="assets('logo-circular-watermarked.png')" />
          </v-avatar>
          <image-upload :fab="true" :vm="this" :add-small="false" :success-function="successUploadImage"
            :label="$t('message.upload_image')"></image-upload>
        </v-card-text>
        <h2 class="pa-4" v-if="productId == 'new'">{{ $t("message.add_product") }}</h2>
        <v-card-text>
          <v-select v-if="productId == 'new'" :items="types" outlined :label="$t('message.product_type')"
            v-model="productType" dense></v-select>
          <v-select v-if="productId == 'new' && product.type == 'service'" :items="intervals" outlined dense
            :label="$t('message.frequency')" v-model="product.interval"></v-select>
          <v-text-field v-if="productId == 'new' && product.interval == 'custom-day-cycle'" type="number" outlined dense
            :label="$t('message.custom_days')" v-model="product.customDaysInterval">
          </v-text-field>
          <v-switch v-if="product.type == 'good'" v-model="product.allowInTablet"
            :label="$t('message.allow_in_tablet')"></v-switch>
          <v-alert type="info" v-if="product.allowInTablet">
            {{ $t('message.allow_in_tablet_explained') }}
          </v-alert>
          <v-switch type="info" v-if="product.type == 'good' && !product.isMembership && stripeReady"
            v-model="product.allowInConnect" :label="$t('message.allow_in_app')"></v-switch>
          <v-alert type="info" v-if="product.allowInConnect">
            {{ $t('message.allow_in_app_explained') }}
          </v-alert>
          <v-switch v-if="false && product.isMembership" v-model="product.allowPublicPurchase"
            :label="$t('message.allow_public_purchase')">
          </v-switch>
          <div class="text-caption" v-if="product.allowInTablet">{{ $t('message.allow_sold_tablet_explained') }}</div>
          <v-switch v-show="false" :disabled="true" v-if="product.type == 'good' && product.isMembership"
            v-model="product.isMembership" :label="$t('message.product_is_membership')"></v-switch>
          <v-card outlined v-if="product.type == 'good' && product.isMembership" class="pa-2 mb-2">
            <div class="gray--text caption-text mb-2">{{ $t('message.membership_days_explained') }}</div>
            <v-select :items="months" outlined
              :label="$t('message.membership_months') + ' (' + $t('message.optional').toLowerCase() + ')'"
              v-model="product.membershipMonths" dense clearable
              :disabled="product.membershipDays > 0 || product.membershipCheckins > 0 ? true : false"></v-select>
            <v-text-field type="number" outlined dense
              :label="$t('message.membership_days') + ' (' + $t('message.optional').toLowerCase() + ')'"
              :disabled="product.membershipMonths > 0 || product.membershipCheckins > 0 ? true : false"
              v-model="product.membershipDays">
            </v-text-field>
            <v-text-field type="number" outlined dense hide-details
              :label="$t('message.membership_checkins') + ' (' + $t('message.optional').toLowerCase() + ')'"
              :disabled="product.membershipDays > 0 || product.membershipMonths > 0 ? true : false"
              v-model="product.membershipCheckins">
            </v-text-field>
            <v-switch hide-details
              v-if="product.membershipDays > 0 || product.membershipMonths > 0 || product.membershipCheckins > 0"
              v-model="product.manualRecurrence" :label="$t('message.keep_membership_visible')">
            </v-switch>
            <div class="text-caption" v-if="product.manualRecurrence">{{ $t('message.keep_membership_visible_explained')
              }}
            </div>
          </v-card>
          <v-text-field class="mt-2" outlined dense :label="$t('message.name')" v-model="product.name"></v-text-field>
          <ProductCategories v-if="product.type == 'good'" v-model="product.category"></ProductCategories>
          <v-text-field v-if="productId != 'new' && product.type == 'service'" outlined dense disabled
            :label="$t('message.frequency')" v-model="intervalText"></v-text-field>
          <v-text-field :disabled="true" v-if="productId != 'new' && product.interval == 'custom-day-cycle'"
            type="number" outlined dense :label="$t('message.custom_days')" v-model="product.customDaysInterval">
          </v-text-field>
          <v-textarea outlined :label="$t('message.description')" v-model="product.description"></v-textarea>
          <div class="mb-2">
            <v-row class="mb-0 pb-2">
              <v-col>
                <v-text-field :disabled="this.productId != 'new' && product.type != 'good'" outlined dense
                  :label="$t('message.price')" v-mask="mask1" v-model="price"></v-text-field>
              </v-col>
              <v-col>
                <v-text-field :disabled="this.productId != 'new' && product.type != 'good'" outlined dense
                  v-mask="mask2" v-model="priceDecimal"></v-text-field>
              </v-col>
              <v-col cols="12" xs="12" sm="2">
                <v-text-field outlined dense disabled v-model="currencyText"
                  :label="$t('message.currency')"></v-text-field>
              </v-col>
            </v-row>
          </div>
          <v-card v-if="product && product.isMembership && product._id" class="mb-2" elevation="0" outlined>
            <v-card-text>
              <v-switch hide-details v-model="restrictAttendance" :label="$t('message.restrict_attendance')"></v-switch>
              <div class="text-caption">
                {{ $t('message.restrict_attendance_explained') }}
              </div>
            </v-card-text>
            <v-card-text v-if="restrictAttendance" class="mt-0 pt-0 mb-0 pb-0">
              <v-subheader>{{ $t('message.by_class_name') }}</v-subheader>
              <AvailableClasses v-model="product.allowedClasses"></AvailableClasses>
              <v-combobox class="mt-2" v-if="product.allowedCusftomClasses" v-model="product.allowedCustomClasses"
                :items="product.allowedCustomClasses" small-chips outlined hide-details dense
                :label="$t('message.custom_names')" :multiple="true" clearable>
              </v-combobox>
              <v-subheader>{{ $t('message.by_class_count') }}</v-subheader>
              <div>
                <div class="mt-1">
                  <v-select outlined dense :items="allowedClassIntervals" v-model="allowedClassCountInterval"
                    :label="$t('message.class_count_interval')" :clearable="true"></v-select>
                </div>
                <div v-if="allowedClassCountInterval">
                  <v-text-field hide-details outlined dense v-model="allowedClassCount" number
                    :label="$t('message.allowed_classes')"></v-text-field>
                  <v-switch hide-details v-model="allowedClassCountSendEmail"
                    :label="$t('message.send_email')"></v-switch>
                  <div class="text-caption ml-1 gray--text">
                    {{ $t('message.allowed_class_count_send_email_explained') }}
                  </div>
                </div>
              </div>
            </v-card-text>
            <v-card-text>
              <v-switch v-if="contracts.length > 0" v-model="assignContract"
                :label="$t('message.assign_contract')"></v-switch>
              <v-autocomplete hide-details v-show="assignContract" v-model="product.documentTemplate"
                :label="$t('message.contract')" :items="contracts" item-text="text" item-value="value" clearable
                outlined dense>
              </v-autocomplete>
              <span v-if="assignContract && assignContract" class="supporting-text">{{
                $t('message.assign_contract_explained') }}</span>
            </v-card-text>
          </v-card>
          <v-card class="pa-6 mb-2" v-if="automaticTaxes" elevation="0" outlined>
            <div class="text-subtitle-2 gray--text">{{ $t('message.manage_taxes') }}</div>
            <div class="text-caption gray--text" v-if="product.taxBehavior">
              <span v-if="product.taxBehavior == taxBehaviors.exclusive">
                {{ $t('message.tax_behavior_excusive') }}
              </span>
              <span v-else-if="product.taxBehavior == taxBehaviors.inclusive">
                {{ $t('message.tax_behavior_inclusive') }}
              </span>
            </div>
            <v-radio-group v-model="product.taxBehavior" class="mb-0 pb-0">
              <v-radio :label="$t('message.none')" :value="taxBehaviors.none"></v-radio>
              <v-radio :label="$t('message.inclusive')" :value="taxBehaviors.inclusive"></v-radio>
              <v-radio :label="$t('message.exclusive')" :value="taxBehaviors.exclusive"></v-radio>
            </v-radio-group>
            <div v-if="product.taxBehavior">
              <v-autocomplete v-if="taxCodes" :items="taxCodes" v-model="product.taxCategory"
                :label="$t('message.tax_category')" item-text="name" item-value="id" outlined dense>
                <template v-slot:item="data">
                  <v-list-item-content class="text-left">
                    <v-list-item-title v-html="data.item.name"></v-list-item-title>
                    <v-list-item-subtitle v-html="data.item.description"></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </div>
          </v-card>
          <v-card class="pa-6 mb-2" v-if="product.type == 'good' && !product.isMembership" elevation="0" outlined>
            <div class="text-subtitle-2 gray--text mb-2">{{ $t('message.item_details') }}</div>
            <v-select v-if="false && altStripeAccounts" outlined dense v-model="product.altStripeAccount"
              :items="altStripeAccounts" item-text="name" item-value="id" clearable
              :label="$t('message.alt_stripe_account')"></v-select>
            <v-text-field class="pb-0 mb-0" :disabled="product.unlimited" outlined dense
              :label="$t('message.inventory_quantity') + ' (' + $t('message.optional') + ')'"
              v-model="product.availableQuantity"></v-text-field>
            <v-switch class="mt-0 pt-0" dense v-model="product.unlimited"
              :label="$t('message.unlimited_quantity')"></v-switch>
            <v-switch v-if="product.type == 'good' && !product.isMembership" v-model="showColorPicker" dense
              :label="$t('message.color') + ' (' + $t('message.optional') + ')'"></v-switch>
            <v-color-picker v-if="product.type == 'good' && showColorPicker && !product.isMembership"
              :hide-mode-switch="true" :hide-inputs="true" mode="hexa" v-model="product.color"></v-color-picker>
            <v-text-field v-if="product.type == 'good' && !product.isMembership" single-line outlined dense
              :label="$t('message.size') + ' (' + $t('message.optional') + ')'" v-model="product.size"></v-text-field>
          </v-card>
          <v-expansion-panels elevation="0" flat
            v-if="product.type == 'good' && !product.unlimited && this.productId != 'new' && !product.isMembership">
            <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
              <v-expansion-panel-header @click="getProductInventory()">
                <v-card-text class="pa-0">
                  <span class="mt-2"><v-icon>mdi-select-multiple</v-icon></span>
                  {{ $t("message.inventory") }}
                </v-card-text>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-skeleton-loader type="list-item-avatar" v-if="loadingInventory"></v-skeleton-loader>
                <div v-else-if="inventory.length == 0" class="text-subtitle-1 text-center">
                  {{ $t("message.no_data") }}
                </div>
                <v-list v-if="inventory.length > 0">
                  <template v-for="(i, index) in inventory">
                    <v-list-item :key="'inventory_' + index">
                      <v-list-item-avatar>
                        <MemberAvatar :name="i.user ? i.user.displayName : null"
                          :image="i.user && i.user.userImageSmall ? i.user.userImageSmall : null">
                        </MemberAvatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon v-if="i.action == 'edit'">mdi-pencil-box-outline</v-icon>
                          <v-icon v-else>mdi-cart-outline</v-icon>
                          <span class="mr-1">Total: {{ i.totalInventory }}</span></v-list-item-title>
                        <v-list-item-subtitle v-if="i.user">
                          {{ i.user.displayName }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>{{ $d(new Date(i.createdAt), "month_day_year", $i18n.locale) }}
                          <span class="secondary--text mr-2 text-subtitle-1">{{
                            i.amount > 0
                              ? "+" + i.amount : i.amount }}
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action v-if="i.transaction" @click="goToTransaction(i.transaction)">
                        <v-icon>mdi-card-search-outline</v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-text class="gray--text text-caption mt-0" v-if="product.type == 'service'">
          {{ $t('message.prices_explained') }}
        </v-card-text>
        <v-card-actions>
          <v-btn :loading="updating" outlined v-if="product._id" v-allowed="'edit_products'" color="primary"
            elevation="0" small @click="addEditProduct()" :disabled="!currency ? true : false">{{ $t('message.update')
            }}</v-btn>
          <v-btn :loading="updating" outlined v-else v-allowed="'add_products'" color="primary" elevation="0" small
            @click="addEditProduct()" :disabled="!currency ? true : false">{{ $t('message.add') }}</v-btn>
          <v-btn outlined v-if="product._id && !isSmallerDisplay() && product.type == 'good' && !product.isMembership"
            v-allowed="'edit_products'" color="primary" elevation="0" small @click="showDuplicateProductDialog()">{{
              $t('message.duplicate') }}</v-btn>
          <v-btn outlined small v-if="!isSmallerDisplay() && product._id && stripeReady" elevation="0" color="primary"
            @click.stop="goToSell()">
            {{ $t("message.sell") }}
          </v-btn>
          <v-btn small outlined
            v-if="!isSmallerDisplay() && product._id && product.type == 'good' && !product.isMembership && stripeReady"
            elevation="0" color="primary" @click.stop="openQRCode()">
            <v-icon>mdi-qrcode</v-icon>
            {{ $t("message.print_qr_code") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" small @click="goBack()">{{ $t('message.back') }}</v-btn>
          <v-btn class="mr-2" elevation="0" small v-if="product._id && !isSmallerDisplay()" color="error"
            @click="showDeleteProductDialog()">{{
              $t('message.delete') }}</v-btn>
        </v-card-actions>
        <v-card-actions v-if="isSmallerDisplay()">
          <v-btn outlined block v-if="product._id && product.type == 'good' && !product.isMembership"
            v-allowed="'edit_products'" color="primary" elevation="0" small @click="showDuplicateProductDialog()">{{
              $t('message.duplicate')
            }}</v-btn>
        </v-card-actions>
        <v-card-actions v-if="product && stripeReady">
          <v-btn outlined small block
            v-if="isSmallerDisplay() && product._id && product.type == 'good' && !product.isMembership" elevation="0"
            color="primary" @click.stop="openQRCode()">
            <v-icon>mdi-qrcode</v-icon>
            {{ $t("message.print_qr_code") }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-if="isSmallerDisplay()">
          <v-btn block class="mr-2" elevation="0" small v-if="product._id" color="error"
            @click="showDeleteProductDialog()">{{
              $t('message.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
      <v-dialog v-model="deleteProductDialog" max-width="90%">
        <v-card class="pa-2">
          <v-card-text>{{ $t('message.delete_product_confirm') }}</v-card-text>
          <v-card-actions>
            <v-btn :loading="deletingProduct" elevation="0" small color="error" @click.stop="deleteProduct()">{{
              $t('message.delete') }}</v-btn>
            <v-btn elevation="0" small color="secondary" @click.stop="deleteProductDialog = false">{{
              $t('message.close')
              }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="duplicateProductDialog" max-width="90%">
        <v-card class="pa-2" v-if="duplicateProduct">
          <v-card-title>{{ $t('message.duplicate') }}</v-card-title>
          <v-card-text>
            <v-row class="mb-0 pb-2">
              <v-col>
                <v-text-field class="mt-2" outlined dense :label="$t('message.name')"
                  v-model="duplicateProduct.name"></v-text-field>
              </v-col>
            </v-row>
            <v-row class="mb-0 pb-2">
              <v-col>
                <v-text-field outlined dense :label="$t('message.price')" v-mask="mask1"
                  v-model="duplicateProduct.price"></v-text-field>
              </v-col>
              <v-col>
                <v-text-field outlined dense v-mask="mask2" v-model="duplicateProduct.priceDecimal"></v-text-field>
              </v-col>
              <v-col cols="12" xs="12" sm="2">
                <v-text-field outlined dense disabled v-model="currencyText"
                  :label="$t('message.currency')"></v-text-field>
              </v-col>
            </v-row>
            <v-row class="mb-0 pb-2">
              <v-col>
                <v-switch class="mt-0 pt-0" dense v-model="duplicateProduct.unlimited"
                  :label="$t('message.unlimited_quantity')"></v-switch>
                <v-text-field class="pb-0 mb-0" :disabled="duplicateProduct.unlimited" outlined dense
                  :label="$t('message.inventory_quantity') + ' (' + $t('message.optional') + ')'"
                  v-model="duplicateProduct.availableQuantity"></v-text-field>
              </v-col>
            </v-row>
            <v-row class="mb-0 pb-2">
              <v-col>
                <v-text-field outlined dense :label="$t('message.size')" v-model="duplicateProduct.size"></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn outlined elevation="0" small color="primary" @click.stop="submitDuplicateProduct()">{{
              $t('message.duplicate')
              }}</v-btn>
            <v-btn elevation="0" small color="secondary" @click.stop="duplicateProductDialog = false">{{
              $t('message.cancel')
              }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <div v-else class="text-center">
      <h2>{{ $t('message.invalid_product') }}</h2>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn elevation="0" @click="navigateTo({ name: 'products' })">{{ $t('message.back')
          }}</v-btn>
        <v-spacer></v-spacer></v-card-actions>
    </div>
  </div>
</template>

<script>
import ImageUpload from "@/components/inputs/ImageUploadCroppie.vue";
import ProductCategories from "@/components/inputs/ProductCategoriesCombo.vue";
import AvailableClasses from "@/components/inputs/ScheduleClassesCombo.vue";
import MemberAvatar from "@/components/displays/MemberAvatar.vue";
import Api from "@/services/Api";
import { Environment } from "@/services/Environment";
import { mask } from "vue-the-mask";

export default {
  directives: {
    mask,
  },
  data() {
    return {
      loading: true,
      productId: this.$route.params.productId,
      formMessage: null,
      productType: null,
      automaticTaxes: false,
      product: null,
      showColorPicker: false,
      productImage: null,
      currency: null,
      currencyText: null,
      intervalText: null,
      availableClasses: [],
      restrictAttendance: false,
      duplicateProductDialog: null,
      duplicateProduct: null,
      price: null,
      mask1: "########",
      altStripeAccounts: null,
      stripeReady: false,
      paymentsReady: false,
      mask2: "##",
      priceDecimal: "00",
      updating: false,
      goods: [],
      subscriptions: [],
      categories: [],
      managedAcademy: this.$store.state.managedAcademy,
      deleteProductDialog: false,
      contracts: [],
      assignContract: false,
      deletingProduct: false,
      taxCodes: null,
      paymentsModule: null,
      inventory: [],
      loadingInventory: false,
      allowedClassCount: null,
      allowedClassCountInterval: null,
      allowedClassCountSendEmail: false,
      taxBehaviors: {
        exclusive: 'exclusive',
        inclusive: 'inclusive',
        none: null,
      },
      types: [
        { text: this.$t("message.goods"), value: "good" },
        { text: this.$t("message.manual_membership"), value: "good_membership" },
      ],
      intervals: [
      ],
      months: [
        { text: "1 " + this.$t("message.month"), value: 1 },
        { text: "2 " + this.$t("message.months"), value: 2 },
        { text: "3 " + this.$t("message.months"), value: 3 },
        { text: "4 " + this.$t("message.months"), value: 4 },
        { text: "5 " + this.$t("message.months"), value: 5 },
        { text: "6 " + this.$t("message.months"), value: 6 },
        { text: "7 " + this.$t("message.months"), value: 7 },
        { text: "8 " + this.$t("message.months"), value: 8 },
        { text: "9 " + this.$t("message.months"), value: 9 },
        { text: "10 " + this.$t("message.months"), value: 10 },
        { text: "11 " + this.$t("message.months"), value: 11 },
        { text: "12 " + this.$t("message.months"), value: 12 },
        { text: "13 " + this.$t("message.months"), value: 13 },
        { text: "14 " + this.$t("message.months"), value: 14 },
        { text: "15 " + this.$t("message.months"), value: 15 },
        { text: "16 " + this.$t("message.months"), value: 16 },
        { text: "17 " + this.$t("message.months"), value: 17 },
        { text: "18 " + this.$t("message.months"), value: 18 },
        { text: "19 " + this.$t("message.months"), value: 19 },
        { text: "20 " + this.$t("message.months"), value: 20 },
        { text: "21 " + this.$t("message.months"), value: 21 },
        { text: "22 " + this.$t("message.months"), value: 22 },
        { text: "23 " + this.$t("message.months"), value: 23 },
        { text: "24 " + this.$t("message.months"), value: 24 },
      ],
      allowedClassIntervals: [
        { text: this.$t("message.weekly"), value: "week" },
        { text: this.$t("message.monthly"), value: "month" },],
      successUploadImage: function (uploadedFileData, vm) {
        if (vm && uploadedFileData) {
          vm.product.image = uploadedFileData.location;
          vm.productImage = uploadedFileData.original;
        }
      },
    };
  },
  components: {
    ImageUpload,
    ProductCategories,
    AvailableClasses,
    MemberAvatar
  },
  created() {
    this.loading = true;
    this.initPage();
    if (this.isAppAdmin())
      this.intervals.unshift({ text: this.$t("message.daily"), value: "day" });
  },
  beforeRouteUpdate(to, from, next) {
    this.productId = to.params.productId;
    this.loading = true;
    this.product = null;
    this.initPage();
    next();
  },
  watch: {
    productType: {
      handler: function (newValue) {
        if (newValue == 'good_membership') {
          this.product.type = 'good';
          this.product.isMembership = true;
          this.product.category = this.$t("message.membership");
          this.product.manualRecurrence = true;
        } else {
          this.product.type = newValue;
          this.product.isMembership = false;
          this.product.category = '';
        }
        this.initTaxCode(this.product);
      },
    },
    showColorPicker: {
      handler: function (newValue) {
        if (!newValue) this.product.color = null;
      },
    },
  },
  methods: {
    submitDuplicateProduct() {
      if (this.duplicateProduct.priceDecimal == "") this.duplicateProduct.priceDecimal = "00";
      else if (this.duplicateProduct.priceDecimal.length == 1)
        this.duplicateProduct.priceDecimal = this.duplicateProduct.priceDecimal + "0";
      this.duplicateProduct.amount = new Number(this.duplicateProduct.price + this.duplicateProduct.priceDecimal);
      this.duplicateProduct.amountDecimal = new Number(
        this.duplicateProduct.price + "." + this.duplicateProduct.priceDecimal
      );
      Api()
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/" + this.product._id + "/duplicate",
          this.duplicateProduct
        )
        .then((response) => {
          const data = response.data;
          if (data && data._id) {
            this.duplicateProductDialog = false;
            this.navigateTo({ name: 'manageproduct', params: { productId: data._id } })
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert(
              "error",
              this.$t("message.error_generic")
            );
        });
    },
    showDuplicateProductDialog() {
      this.duplicateProduct = {
        price: this.price,
        priceDecimal: this.priceDecimal,
        name: this.product.name,
        size: this.product.size,
        availableQuantity: this.product.availableQuantity,
        unlimited: this.product.unlmited
      };
      this.duplicateProductDialog = true;
    },
    goToTransaction(i) {
      if (i) {
        if (i.items && i.items.length > 0)
          this.navigateTo({ name: 'viewitemstransaction', params: { transactionId: i._id } });
        else
          this.navigateTo({ name: 'viewtransaction', params: { sessionId: i._id } })
      }
    },
    getProductInventory() {
      this.loadingInventory = true;
      Api()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/products/" + this.productId + "/inventory"
        )
        .then((response) => {
          this.inventory = response.data.data;
          this.loadingInventory = false;
        })
        .catch(() => {
          this.loadingInventory = false;
        });
    },
    goToSell() {
      this.navigateTo({ name: 'sellproduct', params: { productId: this.productId }, });
    },
    initTaxCode(product) {
      product.taxCategory = 'txcd_99999999';
      if (product.type == 'service')
        product.taxCategory = 'txcd_20030000';
    },
    getTaxCodes() {
      Api()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/tax/codes"
        )
        .then((response) => {
          this.taxCodes = response.data.codes;
        });
    },
    getAltStripeAccounts() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/altstripeaccounts"
        )
        .then((response) => {
          if (response.data && response.data.accounts)
            this.altStripeAccounts = response.data.accounts;
        });
    },
    initPage() {
      this.loading = true;
      if (this.$store.state.managedAcademy) {
        Api()
          .get(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/is_connected/all"
          )
          .then((response) => {
            this.stripeReady = response.data.stripeConnected;
            this.paymentsModule = response.data.paymentsModule;
            this.paymentsReady = response.data.paymentsReady;
            if (this.stripeReady || response.data.paymentsReady) {
              this.types.push(
                { text: this.$t("message.recurrent_subscription"), value: "service" });
              if (this.paymentsModule == 'stripe') {
                this.intervals.push(
                  { text: this.$t("message.custom_day_cycle"), value: "custom-day-cycle" });
                this.intervals = [
                  { text: this.$t("message.weekly"), value: "week" },
                  { text: this.$t("message.bi_weekly"), value: "bi-week" },
                  { text: this.$t("message.monthly"), value: "month" },
                  { text: this.$t("message.bi_yearly"), value: "bi-year" },
                  { text: this.$t("message.yearly"), value: "year" },];
              } else if (this.paymentsModule == 'easypay') {
                this.intervals = [
                  { text: this.$t("message.weekly"), value: "week" },
                  { text: this.$t("message.monthly"), value: "month" }]
              }
            } else {
              this.types = [
                { text: this.$t("message.goods"), value: "good" },
                { text: this.$t("message.membership"), value: "good_membership" },
              ];
            }
          });
        Api()
          .get("admins/academy/" + this.$store.state.selectedAcademy)
          .then((response) => {
            this.currency = response.data.currency;
            this.currencyText = response.data.currency
              ? response.data.currency
              : "???";
          });
        if (this.productId == "new") {
          this.product = {
            taxBehavior: null,
            allowedCustomClasses: [],
            unlimited: false,
            allowedClassCount: {}
          };
          this.loading = false;
        } else {
          Api()
            .get(
              "admins/academy/" +
              this.$store.state.managedAcademy +
              "/products/" +
              this.productId
            )
            .then((response) => {
              this.product = response.data;
              if (!this.product.allowedClassCount || !this.product.allowedClassCount.interval) {
                this.product.allowedClassCount = {};
              } else {
                this.allowedClassCount = this.product.allowedClassCount.count;
                this.allowedClassCountInterval = this.product.allowedClassCount.interval;
                this.allowedClassCountSendEmail = this.product.allowedClassCount.sendEmail;
              }
              if (this.product.allowedClasses && this.product.allowedClasses.length)
                this.restrictAttendance = true;
              if (this.product.allowedCustomClasses && this.product.allowedCustomClasses.length)
                this.restrictAttendance = true;
              else
                this.product.allowedCustomClasses = [];
              if (this.product.allowedClassCount && this.product.allowedClassCount.interval)
                this.restrictAttendance = true;
              if (!this.product.taxBehavior)
                this.product.taxBehavior = null;
              if (this.product.amountDecimal > 0) {
                const amount = this.product.amount + "";
                this.price = amount.substring(0, amount.length - 2);
                this.priceDecimal = amount.substring(
                  amount.length - 2,
                  amount.length
                );
              } else {
                this.price = 0;
                this.priceDecimal = 0;
              }
              if (this.product.type != "service")
                this.getAltStripeAccounts();
              this.automaticTaxes = this.product.automaticTaxes;
              if (this.automaticTaxes)
                this.getTaxCodes();
              this.productImage = this.product.image;
              if (this.product.color) this.showColorPicker = true;
              this.loading = false;
              if (this.product.isMembership) {
                Api()
                  .get("documents/academy/" + this.$store.state.managedAcademy + "/contracts")
                  .then((response) => {
                    const data = response.data;
                    if (data) {
                      for (const c of data.contracts)
                        this.contracts.push({ value: c._id, text: c.name });
                      if (this.product.documentTemplate)
                        this.assignContract = true;
                    }
                  })
                  .catch((e) => {
                    if (e)
                      this.showAlert("error", this.$t("message.error_generic"));
                  });
                if (
                  this.product.amountStripe &&
                  this.product.amountStripe != this.product.amount
                ) {
                  this.showAlert(
                    "error",
                    this.$t("message.corrupted_item"),
                    this.$t("message.corrupted_item_explained"),
                    25000
                  );
                }
                for (const i of this.intervals) {
                  if (this.product.interval == i.value) {
                    this.intervalText = i.text;
                    break;
                  }
                }
              }
            })
            .catch((e) => {
              this.loading = false;
              if (e)
                this.showAlert("error", this.$t("message.error_generic"));
            });
        }
      }
    },
    openQRCode() {
      let purchaseProductUrl = Environment.get("VUE_APP_API_HOST") + '/pub/academy/' + this.$store.state.selectedAcademy + '/product/' + this.productId;
      let buff = new Buffer(purchaseProductUrl);
      const encodedUrl = buff.toString("base64");
      window.open(Environment.get("VUE_APP_ASSETS_HOME") + 'secure/qr/' + encodedUrl + '?sp=product&p=' + this.productId);
    },
    isValidInteger(num) {
      try {
        Number.parseInt(num);
        return true;
      } catch (e) {
        return false;
      }
    },
    showDeleteProductDialog() {
      this.formMessage = null;
      this.deleteProductDialog = true;
    },
    deleteProduct() {
      this.deletingProduct = true;
      Api()
        .delete(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/" +
          this.productId
        )
        .then((response) => {
          if (response) this.navigateTo({ name: "products" });
        })
        .catch((e) => {
          this.deletingProduct = false;
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    addEditProduct() {
      if (this.paymentsModule == 'stripe' && !this.stripeReady && this.product.type == "service") {
        this.showAlert(
          "error",
          this.$t("message.need_connect_stripe_subscriptions"),
          8000
        );
        return;
      } else if (this.paymentsModule == 'easypay' && !this.paymentsReady) {
        this.showAlert(
          "error",
          this.$t("message.payments_not_ready"),
          8000
        );
        return;
      }
      if (!this.product.name || !this.product.type || this.price == null ||
        (this.product.type == 'service' && !this.product.interval)) {
        this.showAlert("error", this.$t("message.fill_all_params"));
        return;
      } else {
        this.updating = true;
        this.formMessage = null;
        if (this.priceDecimal == "") this.priceDecimal = "00";
        else if (this.priceDecimal.length == 1)
          this.priceDecimal = this.priceDecimal + "0";
        this.product.amount = new Number(this.price + this.priceDecimal);
        this.product.amountDecimal = new Number(
          this.price + "." + this.priceDecimal
        );
        if (this.product.amountDecimal == 0 && this.product.type == 'service') {
          this.showAlert("error", this.$t("message.recurrent_requires_amount"));
          return;
        }
        if (this.restrictAttendance && this.allowedClassCountInterval && !isNaN(this.allowedClassCount)) {
          this.product.allowedClassCount = {
            count: new Number(this.allowedClassCount).valueOf(),
            interval: this.allowedClassCountInterval, // month|week
            sendEmail: this.allowedClassCountSendEmail,
          };
        } else {
          this.product.allowedClassCount = null;
        }
        if (this.product._id) {
          Api()
            .patch(
              "admins/academy/" +
              this.$store.state.managedAcademy +
              "/products",
              this.product
            )
            .then((response) => {
              const data = response.data;
              if (data) {
                this.showAlert("success", this.$t("message.updated"));
                this.getProductInventory();
                this.updating = false;
              }
            })
            .catch((e) => {
              if (e)
                this.showAlert(
                  "error",
                  this.$t("message.error_generic")
                );
            });
        } else {
          this.updating = true;
          Api()
            .post(
              "admins/academy/" +
              this.$store.state.managedAcademy +
              "/products",
              this.product
            )
            .then((response) => {
              const data = response.data;
              if (data && data._id) {
                this.productId = data._id;
                this.updating = false;
                this.initPage();
              }
            })
            .catch((e) => {
              if (e)
                this.showAlert(
                  "error",
                  this.$t("message.error_generic")
                );
            });
        }
      }
    },
  },
};
</script>

<style scoped></style>
